import { Button, Loader } from '@mantine/core';
import { HOME } from 'navigation/routes';
import QUERY_KEYS from 'network/config/queryKeys';
import { getMadinaBranches } from 'network/services/madina';
import React from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { branchIdAtom, branchNameAtom } from '../../store/atoms/branchAtom';
import type { MadinaBranch } from 'TYPES/madina';
import { setStoredBranch } from 'utils/branch';

const BranchesPage: React.FC = () => {
  const navigate = useNavigate();
  const [branchId, setBranchId] = useRecoilState(branchIdAtom);
  const [, setBranchName] = useRecoilState(branchNameAtom);

  const { data: branches = [] as MadinaBranch[], isLoading, isError, refetch } = useQuery({
    queryKey: [QUERY_KEYS.MADINA_BRANCHES],
    queryFn: () => getMadinaBranches()
  });

  const handleSelect = (id: number, name: string) => {
    setBranchId(id);
    setBranchName(name);
    setStoredBranch(id, name);
    navigate(HOME);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div>
        <h1 className="text-3xl font-bold mb-2">Select your branch</h1>
        <p className="text-gray-600 mb-8">
          Products, pricing, and delivery depend on your branch. Choose the store closest to you.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader color="blue" />
          </div>
        ) : null}

        {isError ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
            Unable to load branches.
            <button type="button" className="ml-2 underline" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        ) : null}

        <div className="grid gap-4">
          {branches.map(branch => (
            <button
              key={branch.id}
              type="button"
              onClick={() => handleSelect(branch.id, branch.name)}
              className={`text-left rounded-2xl border p-5 transition hover:border-blue-500 hover:shadow-md ${
                branchId === branch.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 bg-white'
              }`}>
              <div className="font-semibold text-lg">{branch.name}</div>
              {branch.address ? <div className="text-sm text-gray-600 mt-1">{branch.address}</div> : null}
            </button>
          ))}
        </div>

        {branchId ? (
          <div className="mt-8">
            <Button variant="light" onClick={() => navigate(HOME)}>
              Continue with selected branch
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BranchesPage;
